import React, { useState, useContext, useEffect } from "react";
import TextField from "@mui/material/TextField";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import useMediaQuery from "@mui/material/useMediaQuery";
import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import { PendoService } from "@services";
import { ConfigurationContext } from "../../../contexts/Configuration";
import { SearchContext } from "../../../contexts/Search";
import MobileSortIcon from "../CommonComponent/MobileSortIcon";
import {UseLabels} from "../CommonComponent/UseLabels";

const FilterCoverage = ({ channels, planSearch, channelFilter, setSortOrder }) => {
  const { appliedConfiguration } = useContext(ConfigurationContext);
  const { benefitType, productId, productName, indicationName } = useContext(SearchContext);
  const [channel, setChannel] = useState(channels[0]);
  const [searchValue, setSearchValue] = useState("");
  const [sortState, setSortState] = useState(0);
  const channelsObjectArray = channels.map((chn) => ({
    id: chn,
    label: chn,
  }));
  const matchesMobileWidth = useMediaQuery("(max-width:1032px)");
  const [typingTimer, setTypingTimer] = useState(null);
  const doneTypingInterval = 3000; //3 seconds
  const { getLabel } = UseLabels();
  useEffect(() => {
    setChannel(channels[0]);
  }, [channels]);
  const handleChannel = (_, value) => {
    if (value) {
      PendoService.getInstance().trackEvent("handleChannel Event", "handleChannel", value, productId, productName, indicationName);
      setChannel(value);
      channelFilter(value);
    }
  };

  const handlePlanSearch = (event) => {
    setSearchValue(event.target.value);

    //pendo track event
    if (event.target.value !== "") {
      clearTimeout(typingTimer);
      setTypingTimer(
        setTimeout(function () {
          PendoService.getInstance().trackEvent("handlePlanSearch Event", "handlePlanSearch", event.target.value, productId, productName, indicationName);
        }, doneTypingInterval)
      );
    } else {
      clearTimeout(typingTimer);
    }

    planSearch(event.target.value);
  };

  const handleClearPlanSearch = () => {
    setSearchValue("");
    planSearch("");
  };

  const handleSortClick = () => {
    const newSortState = (sortState + 1) % 3;
    setSortState(newSortState);
    setSortOrder(newSortState);
  };

  const getResultLevelDisplayName = () => {
    const resultName =
      benefitType?.toLowerCase() === "pharmacy"
        ? appliedConfiguration.coverageFinderConfiguration.pharmacyResultName?.toLowerCase()
        : appliedConfiguration.coverageFinderConfiguration.resultName?.toLowerCase();
    if (resultName === "controller/payer level") {
      return "Search Payer Names";
    } else if (resultName === "formulary level") {
      return "Search Formulary Names";
    } else {
      return "Search Plan Names";
    }
  };

  const getSearchResultsLabel = () => {
    return <span className="norstella-cf-searchResultsLabel" part="cfSearchResultsLabel">{getResultLevelDisplayName()}</span>;
  };

  const getSortAriaLabel = (sortState) => {
    switch(sortState) {
      case 0:
        return "no sort";
      case 1:
        return "sorted ascending";
      case 2:
        return "sorted descending";
      default:
        return "sort";
    }
  };

  return (
    <div className="norstella-cf-flexAlignFilterBar" part="cfDivFilter">
      {matchesMobileWidth && channelsObjectArray.length > 0 && (
        <>
          <label className="norstella-cf-channelLabel" part="cfChannelLabel" htmlFor="ddlChannel">
            {getLabel("cfMobileChannelLabel", "Filter By")}
          </label>
          <div className="norstella-cf-channelDiv">
            <Autocomplete
              onChange={(_, value) => handleChannel(_, value.label)}
              defaultValue={channel}
              disablePortal
              blurOnSelect
              handleHomeEndKeys
              disableClearable
              id="ddlChannel"
              part="cfDdlChannel"
              className="norstella-cf-filterCoverageNativeSelect"
              options={channelsObjectArray}
              renderInput={(params) => <TextField {...params} />}
            />
          </div>
        </>
      )}
      <div className="norstella-cf-searchContainer">
        <TextField
          id="txtPlanSearch"
          placeholder="Search"
          className="norstella-cf-searchPlans norstella-cf-bottomMargin"
          part="cfTxtPlanSearch"
          variant="outlined"
          onChange={handlePlanSearch}
          value={searchValue}
          label={getSearchResultsLabel()}
          sx={{
            input: {
              "&::placeholder": {
                color: "#595959",
                opacity: 1,
              },
            },
          }}
          InputProps={{
            className: "norstella-cf-searchPlansInput",
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className="norstella-cf-filterCoverageSearchIcon" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {searchValue && (
                  <IconButton
                    className="norstella-cf-filterCoverageClearIconButton"
                    aria-label="clear plan search text"
                    onClick={handleClearPlanSearch}
                  >
                    <ClearIcon className="norstella-cf-filterCoverageClearIcon" />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />
        {matchesMobileWidth && (<Button
          variant="contained"
          className="norstella-cf-sortButton"
          id="sortButton"
          onClick={handleSortClick}
          part="cfSortButton"
          aria-label={getSortAriaLabel(sortState)}>
          {<MobileSortIcon benefitTypeId={benefitType?.toLowerCase() === "pharmacy"?2:1} sortState={sortState}></MobileSortIcon>}
        </Button>)}
      </div>
      {!matchesMobileWidth && channelsObjectArray.length > 0 && (
        <div className="norstella-cf-divChannelButtonGroup">
          <ToggleButtonGroup
            exclusive
            value={channel}
            onChange={handleChannel}
            className="norstella-cf-channelButtonGroup"
            part="cfChannelButtonGroup"
            aria-label="Plan Type"
          >
            {channels.map((chn) => (
              <ToggleButton key={chn} value={chn} className="norstella-cf-channelButton" part="cfChannelButton">
                {chn}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </div>
      )}
    </div>
  );
};

export default FilterCoverage;
